import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from './store/hooks'
import {
  selectIsAuthenticated,
  selectAuthLoading,
  selectUser,
} from './store/slices/authSlice'
import { initializeAuth, login, handleLogout as logout } from './services/auth'
import { setLoading } from './store/slices/authSlice'

// Page imports
import { LandingPage } from './pages/LandingPage'

/**
 * Loading component shown during authentication initialization
 */
const LoadingScreen: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: 'var(--color-brand-background)',
      color: 'var(--color-brand-text-primary)',
      fontFamily: 'var(--font-family-primary)',
    }}
    role="status"
    aria-label="Application loading"
  >
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '4px solid var(--color-brand-primary)',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }}
      />
      <p>Initializing SovereignScope...</p>
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
)

/**
 * Placeholder component for unauthenticated users
 */
const LoginPage: React.FC = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  
  console.log('🔐 LoginPage render:', { isAuthenticated })
  
  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    console.log('✅ User already authenticated, redirecting to dashboard')
    return <Navigate to="/dashboard" replace />
  }

  const handleLoginClick = () => {
    try {
      login()
    } catch (error) {
      console.error('Login failed:', error)
      // In development, if Keycloak fails, show an alert
      if (import.meta.env.DEV) {
        alert('Keycloak authentication is not available. Please check if Keycloak is running on http://localhost:8083')
      }
    }
  }

  return (
    <main
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--color-brand-background)',
        color: 'var(--color-brand-text-primary)',
        fontFamily: 'var(--font-family-primary)',
      }}
      id="main-content"
    >
      <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '500px' }}>
        <h1 style={{ marginBottom: '1rem' }}>SovereignScope</h1>
        <p
          style={{
            marginBottom: '2rem',
            color: 'var(--color-brand-text-secondary)',
          }}
        >
          Software Supply Chain Security Platform
        </p>
        
        <div
          style={{
            padding: '2rem',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--color-brand-surface)',
            marginBottom: '1rem',
          }}
        >
          <p style={{ marginBottom: '1.5rem' }}>
            Please authenticate to access your dashboard
          </p>
          
          <button
            onClick={handleLoginClick}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: 'var(--color-brand-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '1rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Login with Keycloak
          </button>
        </div>
        
        {import.meta.env.DEV && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#FEF3C7',
              color: '#92400E',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
            }}
          >
            <strong>Development Mode:</strong> If authentication is not working, 
            make sure Keycloak is running on http://localhost:8083
          </div>
        )}
      </div>
    </main>
  )
}

/**
 * Placeholder component for authenticated users
 */
const DashboardPage: React.FC = () => {
  const user = useAppSelector(selectUser)
  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
  <div
    style={{
      padding: '2rem',
      minHeight: '100vh',
      backgroundColor: 'var(--color-brand-background)',
      color: 'var(--color-brand-text-primary)',
      fontFamily: 'var(--font-family-primary)',
    }}
  >
    <header style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ marginBottom: '0.5rem' }}>SovereignScope Dashboard</h1>
          <p style={{ color: 'var(--color-brand-text-secondary)' }}>
            Welcome back, {user?.firstName || user?.username}!
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--color-semantic-error)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Logout
        </button>
      </div>
    </header>

    <main id="main-content">

    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}
    >
      <div
        style={{
          padding: '1.5rem',
          backgroundColor: 'var(--color-brand-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <h3
          style={{ marginBottom: '1rem', color: 'var(--color-brand-primary)' }}
        >
          Recent Analyses
        </h3>
        <p style={{ color: 'var(--color-brand-text-secondary)' }}>
          View your latest SBOM analyses and vulnerability assessments
        </p>
      </div>

      <div
        style={{
          padding: '1.5rem',
          backgroundColor: 'var(--color-brand-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <h3
          style={{ marginBottom: '1rem', color: 'var(--color-brand-primary)' }}
        >
          Security Overview
        </h3>
        <p style={{ color: 'var(--color-brand-text-secondary)' }}>
          Monitor critical vulnerabilities and compliance status
        </p>
      </div>

      <div
        style={{
          padding: '1.5rem',
          backgroundColor: 'var(--color-brand-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <h3
          style={{ marginBottom: '1rem', color: 'var(--color-brand-primary)' }}
        >
          Reports & Analytics
        </h3>
        <p style={{ color: 'var(--color-brand-text-secondary)' }}>
          Generate comprehensive security and compliance reports
        </p>
      </div>
    </div>

    <div
      style={{
        padding: '1rem',
        backgroundColor: 'var(--color-semantic-info)',
        color: 'white',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center',
      }}
    >
      <strong>Frontend Foundation Ready!</strong>
      <span style={{ marginLeft: '0.5rem' }}>
        The React TypeScript setup is complete with authentication, state
        management, and testing infrastructure.
      </span>
    </div>

    </main>
  </div>
  )
}

/**
 * Protected route wrapper component
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isLoading = useAppSelector(selectAuthLoading)

  console.log('🛡️ ProtectedRoute check:', { isAuthenticated, isLoading })

  if (isLoading) {
    console.log('⏳ ProtectedRoute showing loading screen')
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    console.log('🚫 User not authenticated, redirecting to /login')
    return <Navigate to="/login" replace />
  }

  console.log('✅ User authenticated, showing protected content')
  return <>{children}</>
}

/**
 * Main App component
 */
const App: React.FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const isLoading = useAppSelector(selectAuthLoading)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  // Public routes that don't need authentication
  const publicRoutes = ['/', '/login']
  const isPublicRoute = publicRoutes.includes(location.pathname)

  useEffect(() => {
    // Skip auth initialization for public routes (landing page, login)
    if (isPublicRoute) {
      console.log('🌐 Public route detected, skipping auth initialization')
      dispatch(setLoading(false))
      return
    }

    let isMounted = true // Flag to prevent state updates if component unmounts
    
    const initialize = async (): Promise<void> => {
      try {
        console.log('🚀 Starting authentication initialization')
        if (!isMounted) return
        
        dispatch(setLoading(true))
        const authenticated = await initializeAuth()
        
        if (!isMounted) return
        console.log('✅ Authentication initialization complete:', { authenticated })
      } catch (error) {
        console.error('❌ Failed to initialize application:', error)
        // For production, don't fail silently - but don't redirect to localhost
        if (import.meta.env.PROD) {
          console.warn('Auth initialization failed in production, continuing without auth')
        }
      } finally {
        if (isMounted) {
          dispatch(setLoading(false))
          console.log('🏁 Authentication loading complete')
        }
      }
    }

    initialize()
    
    return () => {
      isMounted = false // Cleanup function
    }
  }, [dispatch, isPublicRoute])

  console.log('🔍 App render state:', { isLoading, isAuthenticated, isPublicRoute, pathname: location.pathname })

  // Show loading screen during app initialization (only for protected routes)
  if (!isPublicRoute && isLoading) {
    console.log('⏳ Showing loading screen')
    return <LoadingScreen />
  }


  return (
    <div className="app">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}

export default App
